import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TableBuilder from '../TableBuilder/TableBuilder';
import {
  fetchCustomers,
  addCustomerAsync,
  updateCustomerAsync,
  resetAddStatus,
  deleteCustomerAsync
} from '../../slices/customersSlice';
import { useNotification } from '../../Helper/NotificationProvider';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConfigProvider, Form, Button } from 'antd';
import AddCustomerModal from './AddCustomerModal';
import { EyeInvisibleOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';

const Customers = () => {
  const root = document.documentElement;
  const primaryColor = getComputedStyle(root)
    .getPropertyValue('--primary-color')
    .trim();

  const dispatch = useDispatch();
  const customers = useSelector(state => state.customers.data);
  const loading = useSelector(state => state.customers.loading);
  const customerStatus = useSelector(state => state.customers.status);

  const [showApiKey, setShowApiKey] = useState(false);

  useEffect(() => {
    if (customerStatus === 'idle') {
      dispatch(fetchCustomers());
    }
  }, [customerStatus, dispatch]);

  const navigate = useNavigate();
  const { openNotification, contextHolder } = useNotification();
  const { t } = useTranslation();

  const [isAddCustomerModalVisible, setIsAddCustomerModalVisible] =
    useState(false);
  const [form] = Form.useForm();

  const columns = [
    { title: 'Name', dataIndex: 'name', width: '10%', editable: true },
    {
      title: 'Rechnungsadresse',
      dataIndex: 'address',
      width: '10%',
      editable: true
    },
    { title: 'Kontakt', dataIndex: 'contact', width: '10%', editable: true },
    { title: 'Email', dataIndex: 'email', width: '10%', editable: true },
    { title: 'Telefon', dataIndex: 'phone', width: '10%', editable: true },
    {
      title: 'Sepioo Account',
      dataIndex: 'sepiooAccount',
      width: '10%',
      editable: true
    },
    {
      title: 'Sepioo Location',
      dataIndex: 'sepiooLocation',
      width: '10%',
      editable: true
    },
    {
      title: (
        <span>
          API Schlüssel{' '}
          {!showApiKey ? (
            <EyeInvisibleOutlined onClick={() => setShowApiKey(!showApiKey)} style={{ cursor: 'pointer' }} />
          ) : (
            <EyeOutlined onClick={() => setShowApiKey(!showApiKey)} style={{ cursor: 'pointer' }} />
          )}
        </span>
      ),
      dataIndex: 'sepiooApiKey',
      width: '10%',
      render: (text) => (
        <span>
          {showApiKey ? text : '••••••••••••••••••••••••'}
        </span>
      ),
      editable: true }
  ];

  const typFilter = ['Name/John Doe', 'City/New York', 'Name/Jane Smith'];
  const searchableColumns = ['name'];
  const sortableColumns = ['name', 'address', 'contact', 'email', 'phone'];
  const columnColors = [{ columnName: 'name', color: 'primary-color' }];

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch, navigate]);

  const handleAddCustomer = async () => {
    try {
      const values = await form.validateFields();
      dispatch(addCustomerAsync(values)).then(() => {
        dispatch(fetchCustomers());
        openNotification(
          'bottomRight',
          "Erfolg",
          "Kunde erfolgreich hinzugefügt.",
          true
        );
        setIsAddCustomerModalVisible(false);
        form.resetFields();
        dispatch(resetAddStatus());
      });
    } catch (error) {
      openNotification(
        'bottomRight',
        "Fehler",
        "Hinzufügen des Kunden fehlgeschlagen.",
        false
      );
    }
  };

  const handleEditCustomer = async (key, values) => {
    try {
      dispatch(updateCustomerAsync({ key, ...values })).then(() => {
        dispatch(fetchCustomers());
        openNotification(
          'bottomRight',
          "Erfolg",
          "Kunde erfolgreich geändert.",
          true
        );
      });
    } catch (error) {
      openNotification(
        'bottomRight',
        "Fehler",
        "Ändern des Kunden fehlgeschlagen.",
        false
      );
    }
  };

  const handleDeleteCustomer = key => {
    try {
      dispatch(deleteCustomerAsync(key)).then(() => {
        dispatch(fetchCustomers());
        openNotification(
          'bottomRight',
          "Erfolg",
          "Kunde erfolgreich gelöscht.",
          true
        );
      });
    } catch (error) {
      openNotification(
        'bottomRight',
        "Fehler",
        "Löschen des Kunden fehlgeschlagen.",
        false
      );
    }
  };
  return (
    <>
      {contextHolder}
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: primaryColor
          },
          components: {
            Typography: {
              titleMarginTop: '0.5em'
            }
          }
        }}
      >
        <div style={{ margin: '15px', marginTop: '10px' }}>
          <Button
            type="primary"
            onClick={() => setIsAddCustomerModalVisible(true)}
            style={{ backgroundColor: 'black', marginBottom: 20 }}
            icon={<PlusOutlined />}
          >
            {'Kunde hinzufügen'}
          </Button>
          <TableBuilder
            title={'Kunden'}
            data={customers}
            columns={columns}
            searchableColumns={searchableColumns}
            sortableColumns={sortableColumns}
            columnColors={columnColors}
            typfilter={typFilter}
            expandable={false}
            loading={loading}
            handleSave={handleEditCustomer}
            handleDelete={handleDeleteCustomer}
          />
          <br />
        </div>
      </ConfigProvider>
      <AddCustomerModal
        form={form}
        visible={isAddCustomerModalVisible}
        onOk={handleAddCustomer}
        onCancel={() => setIsAddCustomerModalVisible(false)}
      />
    </>
  );
};

export default Customers;
